import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';
import { TimeframeService } from '../modules/timeframe/services/timeframe.service';
import { Injectable } from '@angular/core';

/**
 * Add `reuse: true` in route-config data in order to cache a route.
 */
@Injectable({
    providedIn: 'root',
})
export class SimpleRouteReuseStrategy implements RouteReuseStrategy {
    private routeStore = new Map<string, DetachedRouteHandle>();

    constructor(private readonly timeframeService: TimeframeService) {
        timeframeService.selectedTimeframe$().subscribe(() => {
            // invalidate the cache if the timeframe changes
            this.invalidateRouteCache();
        });
    }

    private static shouldReuse(route: ActivatedRouteSnapshot): boolean {
        return route.routeConfig?.data?.reuse ?? false;
    }

    private static getFullPath(route: ActivatedRouteSnapshot): string {
        const segments = route.pathFromRoot.filter((r) => !!r.routeConfig?.path).map((r) => r.routeConfig?.path ?? '');
        return segments.join('/');
    }

    public invalidateRouteCache(): void {
        this.routeStore = new Map<string, DetachedRouteHandle>();
    }

    public shouldDetach(route: ActivatedRouteSnapshot): boolean {
        return SimpleRouteReuseStrategy.shouldReuse(route);
    }

    public store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
        this.routeStore.set(SimpleRouteReuseStrategy.getFullPath(route), handle);
    }

    public shouldAttach(route: ActivatedRouteSnapshot): boolean {
        return SimpleRouteReuseStrategy.shouldReuse(route) && !!this.routeStore.get(SimpleRouteReuseStrategy.getFullPath(route));
    }

    public retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        const path = SimpleRouteReuseStrategy.getFullPath(route);
        return this.routeStore.get(path) ?? null;
    }

    public shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        return future.routeConfig === curr.routeConfig;
    }
}
